import { Component, OnDestroy, OnInit } from '@angular/core';
import { CsAlertBoxRef } from '@cbuild/alert-box';
import { ApplicationService } from '@cbuild/core';
import { combineLatest, Subject, take, takeUntil } from 'rxjs';
import { CsSocket } from '../../../services/ws/cs-socket';
import { CsSocketState } from '../../../services/ws/socket-config';
import { WebSocketService } from '../../../services/ws/web-socket.service';
import { CallCenterService } from '../call-center.service';
import { CallDropPopupComponent } from '../call-drop-popup/call-drop-popup.component';
import { CallResponseModel } from '../models/call-response-model';
import { CsDialogService, DialogRef } from '@cbuild/dialog';
@Component({
  selector: 'cs-call-center',
  standalone: false,
  templateUrl: './call-center.component.html',
  styleUrls: ['./call-center.component.scss']
})
export class CallCenterComponent implements OnInit, OnDestroy {
  private $onDestroy = new Subject();
  private socketCallEvents?: CsSocket<CallResponseModel>;
  public pendingCallUpdates: CallResponseModel | undefined;
  public socketState: CsSocketState = 'Initializing';
  public showQueueControls: boolean;
  private alertBoxKillQueue: CsAlertBoxRef | undefined;
  private callUpdateRef: DialogRef<CallDropPopupComponent, {}> | undefined;
  public icon: 'call-inbound' | 'call-outbound' | 'status-call' = 'status-call';
  public isCallConferenceEnabled: boolean = false;

  constructor(
    public service: CallCenterService,
    private dialogService: CsDialogService,
    public webSocketService: WebSocketService,
    private applicationService: ApplicationService
  ) {}

  public ngOnInit(): void {
    this.applicationService.$userInfo
      .pipe(takeUntil(this.$onDestroy))
      .subscribe(_ => {
        combineLatest([
          this.service.getCallUpdate(null),
          this.service.getCallConferenceEnabled(_.cloudTelePhoneType)
        ])
          .pipe(take(1))
          .subscribe(([res, callConference]) => {
            if (res) {
              this.pendingCallUpdates = res;
              this.isCallConferenceEnabled =
                callConference.isCallConferenceEnabled;
              this.openCallUpdateDialog();
            }
          });
      });

    this.webSocketService.$socketConnected
      .pipe(take(1), takeUntil(this.$onDestroy))
      .subscribe(() => {
        this.socketState = 'Connected';
        this.initializeSocket();
      });
  }

  private getCallUpdate(leadId: number | null): void {
    this.service
      .getCallUpdate(leadId)
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.pendingCallUpdates = res;
          if (this.callUpdateRef) {
            this.callUpdateRef!.componentInstance.lead = {
              ...res,
              ...{ isCallConferenceEnabled: this.isCallConferenceEnabled }
            };
          } else {
            this.openCallUpdateDialog();
          }
        }
      });
  }

  public initializeSocket(): void {
    this.socketCallEvents = this.webSocketService.getCsSocket<
      CallResponseModel
    >('call-events');
    this.socketCallEvents.onPush.pipe(takeUntil(this.$onDestroy)).subscribe({
      next: res => {
        this.updateView(res);
      },
      complete: () => {
        this.socketState = 'Disconnected';
      }
    });
  }

  public get isSocketDisconnected(): boolean {
    return this.socketState === 'Disconnected';
  }

  public updateView(callResponse: CallResponseModel): void {
    if (
      (this.pendingCallUpdates?.leadId === callResponse.leadId &&
        this.callUpdateRef) ||
      !this.callUpdateRef
    ) {
      this.getCallUpdate(callResponse.leadId);
    }
  }

  public openCallUpdateDialog(): void {
    if (this.callUpdateRef) {
      return;
    }
    this.callUpdateRef = this.dialogService.open<CallDropPopupComponent, {}>(
      CallDropPopupComponent,
      {
        title: 'Call Feedback',
        data: {
          ...this.pendingCallUpdates,
          ...{ isCallConferenceEnabled: this.isCallConferenceEnabled }
        },
        maxHeight: '98%',
        height: '98%',
        maxWidth: '98%',
        width: '92%'
      }
    );
    const sub = this.callUpdateRef.afterClosed.subscribe(res => {
      this.callUpdateRef = undefined;
      if (res) {
        this.pendingCallUpdates = undefined;
        this.getCallUpdate(null);
      } else {
        this.openCallUpdateDialog();
      }
      sub.unsubscribe();
    });
  }

  public stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  public ngOnDestroy(): void {
    this.socketCallEvents?.close();
    this.alertBoxKillQueue?.close();
  }
}
